/**
 * TeamInsightsDashboard — manager "roster view" for AI performance insights.
 *
 * Lists the manager's direct reports with a color-coded deviation badge
 * (Aligned / Moderate Deviation / High Deviation) and the model's warning
 * flags, so a manager can quickly spot who needs a closer review. Clicking a
 * row opens the detailed `EmployeeInsightCard`.
 *
 * The open employee is stored in the `employee` query param so the browser
 * back button returns to this roster instead of the previous route.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import PageTitle from "../../../components/Page Component/PageTitle";
import { managerTeamPredictionsUrl } from "../../../Service/Apis";
import { EmployeePredictionListItem } from "./Types/MLAnalyticsTypes";
import DeviationBadge from "./components/DeviationBadge";
import RemarksChips from "./components/RemarksChips";
import EmployeeInsightCard from "./EmployeeInsightCard";

const riskFlagColor: Record<string, "success" | "warning" | "error" | "default"> = {
  Low: "success",
  Medium: "warning",
  High: "error",
};

const formatRating = (value: number | null | undefined): string =>
  value === null || value === undefined ? "—" : value.toFixed(2);

interface TeamInsightsDashboardProps {
  /**
   * Set to true when rendering inside another page (e.g. a tab panel) that
   * already accounts for the fixed navbar's height, to avoid double spacing.
   */
  embedded?: boolean;
  /** Roster endpoint to fetch — defaults to the manager's direct reports. */
  predictionsUrl?: string;
  /** KPI averages endpoint used as the baseline when a row is opened — defaults to the manager's team average. */
  kpiAveragesUrl?: string;
  /** Intro copy shown under the page title. */
  description?: string;
  /** Empty-state copy shown when there is no data. */
  emptyMessage?: string;
}

const TeamInsightsDashboard: React.FC<TeamInsightsDashboardProps> = ({
  embedded = false,
  predictionsUrl = managerTeamPredictionsUrl,
  kpiAveragesUrl,
  description = "AI-assisted performance insights for your team, based on predicted vs. actual manager ratings and supporting HR/attendance/utilization data.",
  emptyMessage = "No performance insight data is available for your team yet.",
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState<EmployeePredictionListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const selectedEmployeeId = searchParams.get("employee");
  /** Set when this screen pushed the detail URL, so in-page back can pop it. */
  const detailHistoryPushed = useRef(false);

  const openEmployee = (employeeId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("employee", employeeId);
    detailHistoryPushed.current = true;
    setSearchParams(next);
  };

  const closeEmployee = () => {
    if (detailHistoryPushed.current) {
      detailHistoryPushed.current = false;
      navigate(-1);
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete("employee");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const fetchTeamPredictions = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(predictionsUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRows(response.data || []);
      } catch (err) {
        setError("Failed to load performance insights.");
      } finally {
        setLoading(false);
      }
    };

    fetchTeamPredictions();
  }, [predictionsUrl]);

  const filteredRows = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => (row.employee_name || "").toLowerCase().includes(query));
  }, [rows, searchTerm]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  if (selectedEmployeeId) {
    return (
      <EmployeeInsightCard
        employeeId={selectedEmployeeId}
        onBack={closeEmployee}
        embedded={embedded}
        kpiAveragesUrl={kpiAveragesUrl}
      />
    );
  }

  return (
    <Box sx={{ mt: embedded ? 0 : 10, p: 3 }}>
      <PageTitle>Performance Insights</PageTitle>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && rows.length === 0 && (
        <Alert severity="info">{emptyMessage}</Alert>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          <TextField
            size="small"
            placeholder="Search by employee name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2, width: { xs: "100%", sm: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {filteredRows.length === 0 ? (
            <Alert severity="info">No employees match "{searchTerm}".</Alert>
          ) : (
            <TableContainer component={Paper} elevation={2}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Predicted</TableCell>
                    <TableCell>Actual</TableCell>
                    <TableCell>Alignment</TableCell>
                    <TableCell>Risk</TableCell>
                    <TableCell>Warning Flags</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRows.map((row) => (
                    <TableRow
                      key={row.employee_id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => openEmployee(row.employee_id)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {row.employee_name || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.employee_code}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.designation_name || "—"}</TableCell>
                      <TableCell>{formatRating(row.predicted_manager_rating)}</TableCell>
                      <TableCell>{formatRating(row.manager_overall_rating)}</TableCell>
                      <TableCell>
                        <DeviationBadge category={row.prediction_gap_category} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.risk_flag || "—"}
                          color={riskFlagColor[row.risk_flag] || "default"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <RemarksChips remarks={row.remarks} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={page}
                onPageChange={(_e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default TeamInsightsDashboard;
